import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { FeaturedLives as FeaturedLivesComponent } from "@domestika-dev/prudencio/dist/molecules/DomestikaLiveCarousels";
import { Flex } from "@domestika-dev/prudencio/dist/atoms/Layout";
import * as DomestikaLiveTypes from "../../constants/domestikaLiveTypes";
import { LiveSessionContext } from "../../context/LiveSessionContext";
import { useLanguagesList } from "../../customHooks/useLanguagesList";
import { getAssetPath } from "../../utils/assetsUtils";
import { defaultStreamer, mapTeacher, isTeacher, liveRedirect } from "./utils";
import {
  injectSmoothScrollPolyfill,
  getClientWindowSize,
  WindowSizes,
} from "../../utils/windowUtils";

const IMG_BREAKPOINTS = {
  [WindowSizes.XS]: 288,
  [WindowSizes.SM]: 596,
  [WindowSizes.MD]: 770,
  [WindowSizes.LG]: 980,
};

export const FeaturedLives = () => {
  const intl = useIntl();

  const { featuredLives, user } = useContext(LiveSessionContext);

  const { getLanguagesList } = useLanguagesList();
  const languagesList = useRef(getLanguagesList());

  const [carouselLoading, setCarouselLoading] = useState(true);
  const [carouselItems, setCarouselItems] = useState([]);
  const liveLabelRef = useRef(intl.formatMessage({ id: "live_sessions.carousel_card.now_live" }));
  const windowWidthRef = useRef(window.innerWidth);

  const getLanguageLabel = useCallback(
    (language) => languagesList.current?.find(({ value }) => value === language)?.label,
    [languagesList]
  );

  const mapItems = useCallback(async () => {
    await injectSmoothScrollPolyfill();

    const mappedItems = featuredLives.map(
      ({
        id,
        title,
        scheduleDate,
        liveType,
        language,
        canAccess,
        numberOfViewers,
        coverImage,
        teachers,
        course,
      }) => {
        const streamers = teachers?.length ? teachers.map(mapTeacher) : [defaultStreamer];

        let locked = false;
        let lockedTooltip = null;
        let student = false;
        let studentTooltip = null;
        let viewers = null;
        let startedAt = null;

        if (liveType === DomestikaLiveTypes.DOMESTIKA_LIVE) {
          locked = !canAccess;
          if (locked) {
            lockedTooltip = intl.formatMessage(
              { id: "live_sessions.carousel_card.locked_tooltip" },
              { course: course.title }
            );
          }

          student = canAccess && !isTeacher(course, user);
          if (student) {
            studentTooltip = intl.formatMessage(
              { id: "live_sessions.carousel_card.unlocked_tooltip" },
              { course: course.title }
            );
          }

          viewers = numberOfViewers || 0;
          startedAt = scheduleDate;
        }

        return {
          liveLabel: liveLabelRef.current,
          locked,
          lockedTooltip,
          student,
          studentTooltip,
          liveInfo: {
            startedAt,
            language: getLanguageLabel(language),
            title,
            viewers,
            coverUrl: getAssetPath(
              coverImage,
              IMG_BREAKPOINTS[getClientWindowSize(windowWidthRef.current)]
            ),
          },
          streamers,
          onClick: () => {
            liveRedirect[liveType](id, course?.id);
          },
        };
      }
    );

    setCarouselItems(mappedItems);
    setCarouselLoading(false);
  }, [featuredLives, user]);

  useEffect(() => {
    if (featuredLives?.length && user?.id !== undefined) {
      mapItems();
    }
  }, [featuredLives, user]);

  return (
    <Flex
      width="100%"
      backgroundColor="sonar"
      marginTop={[8, 8, "80px"]}
      paddingY={[5, 8, 9, "120px"]}
      overflow="hidden"
    >
      <FeaturedLivesComponent items={carouselItems} loading={carouselLoading} />
    </Flex>
  );
};

export default FeaturedLives;
